import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BadgeCheck } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GeometricPattern } from '@/layouts/PublicShell';

/**
 * Shell for sign-in, registration and 2FA enrolment.
 *
 * Split layout: the form on one side and the reason to trust it on the other.
 * The brand panel is hidden below `lg` rather than stacked — on a phone it
 * would push the form, which is the only thing anyone came here for, below the
 * fold.
 */
export function AuthShell({
  title,
  description,
  children,
  aside,
  footer,
  wide,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  aside?: ReactNode;
  footer?: ReactNode;
  /** Registration needs more room than sign-in. */
  wide?: boolean;
}) {
  return (
    <div className="flex min-h-screen bg-background lg:h-screen lg:overflow-hidden">
      {/* Brand panel */}
      <aside className="relative hidden w-[44%] max-w-2xl shrink-0 flex-col justify-between overflow-hidden bg-foreground p-10 text-background lg:flex xl:p-14">
        <GeometricPattern className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] text-background/[0.05]" />
        <GeometricPattern className="pointer-events-none absolute -bottom-32 -left-24 h-[380px] w-[380px] text-background/[0.04]" />
        <div className="pointer-events-none absolute left-1/3 top-1/4 h-72 w-72 rounded-full bg-primary/25 blur-[100px]" />

        <Link to="/" className="wordmark relative z-10 text-2xl">
          AAF-UNIFEMGA
        </Link>

        <div className="relative z-10 max-w-md">
          <h2 className="font-heading text-3xl font-bold leading-tight xl:text-4xl">
            Borrow ₦500,000.
            <span className="mt-1 block text-primary">Repay ₦500,000.</span>
          </h2>
          <p className="mt-5 leading-relaxed text-background/60">
            Interest-free lending from the Aliu Abdulkabir Foundation, facilitated by the Obafemi
            Awolowo University Muslim Graduates&rsquo; Association. Not a promotional rate — a
            principle.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              'No interest, ever',
              'No processing or service fees',
              'No late payment penalties',
              'Guaranteed by members who know you',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm font-medium">
                <BadgeCheck size={17} className="shrink-0 text-primary" />
                {item}
              </li>
            ))}
          </ul>

          {aside}
        </div>

        <p className="relative z-10 text-xs text-background/35">
          © {new Date().getFullYear()} AAF-UNIFEMGA. All rights reserved.
        </p>
      </aside>

      {/* Form panel */}
      <div className="flex min-w-0 flex-1 flex-col lg:h-screen">
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground-secondary transition-colors hover:text-primary"
          >
            <ArrowLeft size={15} />
            <span className="wordmark text-lg text-foreground lg:hidden">AAF-UNIFEMGA</span>
            <span className="hidden lg:inline">Back to site</span>
          </Link>
          <ThemeToggle />
        </header>

        {/*
          The scroll container must not centre its own overflow. `items-center`
          on a scrolling flex box pushes content taller than the box out of
          both ends, and the top half ends up above scrollTop 0 where nothing
          can reach it. Centring moves to an inner wrapper with min-h-full: it
          centres a short form and simply grows past a tall one.
        */}
        <main className="flex-1 lg:overflow-y-auto">
          <div className="flex min-h-full items-center justify-center px-5 py-8 sm:px-8 sm:py-12">
            <div className={wide ? 'w-full max-w-xl' : 'w-full max-w-md'}>
              <div className="mb-7">
                <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                  {title}
                </h1>
                {description && (
                  <p className="mt-2 leading-relaxed text-foreground-secondary">{description}</p>
                )}
              </div>

              {children}

              {footer && <div className="mt-6">{footer}</div>}

              {/* The one line worth repeating on every auth screen. */}
              <p className="mt-8 flex items-center justify-center gap-2 rounded-xl bg-success-soft px-4 py-3 text-center text-xs leading-relaxed text-success-soft-foreground lg:hidden">
                <BadgeCheck size={14} className="shrink-0" />
                AAF-UNIFEMGA loans are interest-free. You repay exactly what you borrow.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
